import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router";
import {
  mockProjects,
  IProject,
  SubProject,
  ImpactData,
  ProcessData,
  KeyLearningsData,
  TestimonialData,
  GalleryData,
} from "../../utils/mockProjects";
import { motion, useScroll, useTransform, useSpring } from "motion/react";
import { Subheader } from "./Subheader";
import { Subprojects } from "./Subprojects";
import { Impact } from "./Impact";
import { Process } from "./Process";
import { Keylearnings } from "./Keylearnings";
import { Recognition } from "./Recognition";
import { Testimonials } from "./Testimonials";
import { Gallery } from "./Gallery";
import { Taggline } from "./Taggline";
import { useCursor } from "../../context/cursorUtils";

export const CaseStudy: React.FC = () => {
  const contentRef = useRef<HTMLDivElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);
  const [isVideo, setIsVideo] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const rect = contentRef.current.getBoundingClientRect();
        setIsSticky(rect.top <= 0);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const checkVideoState = () => {
      if (bgRef.current) {
        const rect = bgRef.current.getBoundingClientRect();
        if (rect.width >= window.innerWidth) {
          setIsVideo(true);
        } else {
          setIsVideo(false);
        }
      }
    };

    // Initial check
    checkVideoState();

    // Listen to both resize and scroll events
    const handleResize = () => checkVideoState();
    const handleScroll = () => checkVideoState();

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll);

    // Use ResizeObserver to watch for changes in the bgRef element itself
    let resizeObserver: ResizeObserver | null = null;
    if (bgRef.current) {
      resizeObserver = new ResizeObserver(checkVideoState);
      resizeObserver.observe(bgRef.current);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  const { projectId } = useParams();
  const [project, setProject] = useState<IProject>(mockProjects[0]);

  useEffect(() => {
    const foundProject = mockProjects.find((p) => p.id === projectId);
    if (foundProject) {
      setProject(foundProject);
    }
  }, [projectId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { scrollY } = useScroll();
  const threshold = 100; // Scroll threshold to trigger animation

  const progress = useTransform(scrollY, [0, threshold], [0, 1], {
    clamp: true,
  });
  const smoothProgress = useSpring(progress, {
    stiffness: 200,
    damping: 30,
  });

  const imgWidth =
    window.innerWidth >= 1280
      ? "25%"
      : window.innerWidth >= 1024
        ? "50%"
        : "80%";

  const imageWidth = useTransform(smoothProgress, [0, 1], [imgWidth, "100%"]);
  const imageHeight = useTransform(smoothProgress, [0, 1], ["75%", "100%"]);
  const imageRadius = useTransform(smoothProgress, [0, 1], ["333px", "0px"]);
  const titleSize = useTransform(
    smoothProgress,
    [0, 1],
    [
      "clamp(2.5rem, 8vw, 100px)", // Large, responsive
      "clamp(1.5rem, 4vw, 30px)", // Small, responsive
    ],
  );
  const titleY = useTransform(smoothProgress, [0, 1], ["0", "-45vh"]);
  const labelsOpacity = useTransform(smoothProgress, [0, 1], [1, 0]);
  const { setIsHovering, setHoverText } = useCursor();

  const currentCaseStudy: IProject | undefined = mockProjects?.find(
    (project) => project.id === projectId,
  );

  return (
    <div className="relative w-full flex flex-col justify-center items-center">
      <div
        onMouseEnter={() => {
          setIsHovering(true);
          setHoverText("Scroll");
        }}
        onMouseLeave={() => {
          setIsHovering(false);
          setHoverText("");
        }}
        className="sticky w-full top-0 h-screen flex justify-center items-center overflow-hidden flex-col lg:py-20 py-8"
      >
        {" "}
        <motion.div
          ref={bgRef}
          className="absolute flex justify-center z-0 items-start w-screen"
          style={{
            width: imageWidth,
            height: imageHeight,
            borderRadius: imageRadius,
            backgroundImage: `url(${project.media.cover})`,
            backgroundSize: "cover",
            backgroundPositionX: "50%",
          }}
          animate={{
            opacity: isVideo ? 0 : 1,
          }}
          transition={{
            opacity: { duration: 0.3, ease: "easeInOut" },
          }}
        />
        <motion.video
          className="absolute w-screen h-screen object-cover z-0"
          autoPlay
          loop
          muted
          style={{
            width: imageWidth,
            height: imageHeight,
            borderRadius: imageRadius,
          }}
          animate={{
            opacity: isVideo ? 1 : 0,
          }}
          transition={{
            opacity: { duration: 0.3, ease: "easeInOut" },
          }}
          src={project.media.thumbnailVideo}
        />
        <motion.div
          className={`w-fit lg:h-[40vh] h-full flex flex-col gap-4 items-center justify-between uppercase`}
        >
          <motion.label
            className="text-wrap w-[8%] font-gilroy-light self-start -z-50"
            style={{ opacity: labelsOpacity }}
            transition={{ opacity: { duration: 0.3, ease: "easeInOut" } }}
          >
            {project.subTitle}
          </motion.label>
          <motion.label
            className="xl:text-nowrap text-wrap text-center font-gilroy-light z-50"
            style={{
              fontSize: titleSize,
              y: titleY,
            }}
            transition={{
              fontSize: { duration: 0.5, ease: "easeInOut" },
              y: { duration: 0.5, ease: "easeInOut" },
            }}
          >
            {project.title}
          </motion.label>
          <motion.label
            className="text-wrap lg:w-[8%] w-[100px] font-gilroy-light self-end -z-50"
            style={{ opacity: labelsOpacity }}
            transition={{ opacity: { duration: 0.3, ease: "easeInOut" } }}
          >
            {project?.address}
          </motion.label>
        </motion.div>
      </div>

      <div
        ref={contentRef}
        className={`w-full z-50 h-fit bg-black lg:px-24 md:px-16 px-8 py-10 bg-cover bg-center bg-no-repeat flex flex-col items-center justify-start gap-24 overflow-x-hidden ${isSticky ? "overflow-y-auto" : "overflow-hidden"}`}
      >
        {/* <Overview projectDetails={project} /> */}
        <Taggline
          tagline={currentCaseStudy?.tagline as string}
          keywords={currentCaseStudy?.keywords as string[]}
        />

        <Subheader
          clients={currentCaseStudy?.clients as string[]}
          partner={currentCaseStudy?.partner as string}
          deliverable={currentCaseStudy?.deliverable as string}
          subheaderImage={currentCaseStudy?.subheaderImage as string}
        />

        <Subprojects
          subProjects={
            currentCaseStudy?.subProjects as {
              content: SubProject[];
              images: string[];
            }
          }
        />

        <Impact
          impact={currentCaseStudy?.impact as ImpactData}
          containerRef={contentRef}
        />

        <Process
          processData={currentCaseStudy?.process as ProcessData}
          galleryImages={currentCaseStudy?.gallery?.galleryImages as string[]}
          bgColor={currentCaseStudy?.impact.bgColor as string}
        />
        <Keylearnings
          keyLearnings={currentCaseStudy?.keyLearnings as KeyLearningsData}
        />
        <Recognition
          awards={currentCaseStudy?.awards}
          featured={currentCaseStudy?.featured}
        />
        <Testimonials
          testimonials={currentCaseStudy?.testimonials as TestimonialData}
        />
        <Gallery gallery={currentCaseStudy?.gallery as GalleryData} />

        {/* <ProjectCarouse currentProjectId={project?.id} /> */}
      </div>
    </div>
  );
};
